import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import axios, { API } from '@/utils/api';

const API_BASE = API.replace('/api', '');

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValidating(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API_BASE}/api/auth/verify-reset-token/${token}`);
        setTokenValid(res.data.valid);
        setMaskedEmail(res.data.email || '');
      } catch (err) {
        setTokenValid(false);
      } finally {
        setIsValidating(false);
      }
    };
    
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    try {
      setIsSubmitting(true);
      const res = await axios.post(`${API_BASE}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      
      if (res.data.success) {
        setResetSuccess(true);
        toast.success('Password reset successfully!');
      } else {
        toast.error(res.data.message || 'Failed to reset password');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isValidating) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-b from-[#1a0527] via-[#2D063E] to-[#1a0527] flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <img src="/new-logo-homepage.png" alt="UnCloned® Journal" className="h-12 mx-auto mb-6" />
            <div className="w-8 h-8 border-2 border-[#df1283] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#FDF2F8]/70">Validating reset link...</p>
          </div>
        </div>
        <footer className="py-6 border-t border-[#4A0A66]/30">
          <div className="flex items-center justify-center gap-4 text-xs text-[#FDF2F8]/40">
            <Link to="/privacy" className="hover:text-[#0FFFE2] transition-colors">Privacy Policy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-[#0FFFE2] transition-colors">Terms of Service</Link>
          </div>
          <p className="text-center text-xs text-[#FDF2F8]/30 mt-2">
            © {new Date().getFullYear()} UnCloned® Media. All rights reserved.
          </p>
        </footer>
      </div>
    );
  }

  // Invalid or missing token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-b from-[#1a0527] via-[#2D063E] to-[#1a0527] flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="text-center mb-6">
              <img src="/new-logo-homepage.png" alt="UnCloned® Journal" className="h-12 mx-auto" />
            </div>
            <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Invalid Reset Link</h1>
              <p className="text-[#FDF2F8]/70 mb-6">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <Link to="/login">
                <Button className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <footer className="py-6 border-t border-[#4A0A66]/30">
          <div className="flex items-center justify-center gap-4 text-xs text-[#FDF2F8]/40">
            <Link to="/privacy" className="hover:text-[#0FFFE2] transition-colors">Privacy Policy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-[#0FFFE2] transition-colors">Terms of Service</Link>
          </div>
          <p className="text-center text-xs text-[#FDF2F8]/30 mt-2">
            © {new Date().getFullYear()} UnCloned® Media. All rights reserved.
          </p>
        </footer>
      </div>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-b from-[#1a0527] via-[#2D063E] to-[#1a0527] flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="text-center mb-6">
              <img src="/new-logo-homepage.png" alt="UnCloned® Journal" className="h-12 mx-auto" />
            </div>
            <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Password Reset!</h1>
              <p className="text-[#FDF2F8]/70 mb-6">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <Link to="/login">
                <Button className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white w-full">
                  Go to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <footer className="py-6 border-t border-[#4A0A66]/30">
          <div className="flex items-center justify-center gap-4 text-xs text-[#FDF2F8]/40">
            <Link to="/privacy" className="hover:text-[#0FFFE2] transition-colors">Privacy Policy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-[#0FFFE2] transition-colors">Terms of Service</Link>
          </div>
          <p className="text-center text-xs text-[#FDF2F8]/30 mt-2">
            © {new Date().getFullYear()} UnCloned® Media. All rights reserved.
          </p>
        </footer>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#1a0527] via-[#2D063E] to-[#1a0527] flex flex-col">
      {/* Main content - centered */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src="/new-logo-homepage.png" alt="UnCloned® Journal" className="h-12 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white">Reset Your Password</h1>
            {maskedEmail && (
              <p className="text-[#FDF2F8]/50 text-sm mt-1">for {maskedEmail}</p>
            )}
          </div>

          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password */}
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDF2F8]/40" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="pl-10 pr-10 bg-[#4A0A66]/30 border-[#4A0A66] text-white placeholder:text-[#FDF2F8]/30"
                    required
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#FDF2F8]/40 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-[#FDF2F8]/40 mt-1">
                  Min 8 characters, 1 uppercase, 1 lowercase, 1 number
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDF2F8]/40" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="pl-10 bg-[#4A0A66]/30 border-[#4A0A66] text-white placeholder:text-[#FDF2F8]/30"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting || !newPassword || !confirmPassword}
                className="w-full bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white py-2.5"
                data-testid="reset-password-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-[#0FFFE2] hover:underline">
                <ArrowLeft className="w-3 h-3 inline mr-1" />
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer - at bottom */}
      <footer className="py-6 border-t border-[#4A0A66]/30">
        <div className="flex items-center justify-center gap-4 text-xs text-[#FDF2F8]/40">
          <Link to="/privacy" className="hover:text-[#0FFFE2] transition-colors">Privacy Policy</Link>
          <span>•</span>
          <Link to="/terms" className="hover:text-[#0FFFE2] transition-colors">Terms of Service</Link>
        </div>
        <p className="text-center text-xs text-[#FDF2F8]/30 mt-2">
          © {new Date().getFullYear()} UnCloned® Media. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default ResetPasswordPage;
